import { HTML } from '@yleisradio/areena-types';
import { FunctionComponent, useCallback, useRef, useState } from 'react';
import Script from 'next/script';
import classNames from 'classnames';
import styles from './VideoFeedCarousel.module.scss';
import {
  MediaCarousel,
  MediaCarouselHandle,
} from '@yleisradio/lyhyet-web-items';
import { useTunnusContext } from 'contexts/TunnusContext';
import { useUILanguage } from 'hooks/useUILanguage';
import { usePlayerScriptUrl } from 'components/Player/YlePlayer/usePlayerScriptUrl';
import logger from 'services/logger';
import { PlayerSdk } from '@yleisradio/player-types';
import { Container } from 'components/Container';
import { useVideoFeedTracking } from './useVideoFeedTracking';
import { apiGatewayApiProperties } from 'services/properties/frontend';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useNearViewport } from 'hooks/useNearViewport';
import { ScrollButton } from 'components/ScrollButton';
import '@yleisradio/lyhyet-web-items/styles.css';

interface VideoFeedCarouselProps {
  content: HTML;
  contentIndex: number;
}

// Make sure these match the amount of carousel items shown, determined by
// carousel item width in VideoFeedCarousel.module.scss.
function useVisibleItemsCount(): number {
  const isDesktop = useBreakpoint('desktop');
  const isDesktopSmall = useBreakpoint('desktop-small');
  const isTablet = useBreakpoint('tablet');
  const isMobileLandscape = useBreakpoint('mobile-landscape');

  if (isDesktop) return 6;
  if (isDesktopSmall) return 5;
  if (isTablet) return 3;
  if (isMobileLandscape) return 2;
  return 1;
}

export const VideoFeedCarousel: FunctionComponent<VideoFeedCarouselProps> = ({
  content,
  contentIndex,
}) => {
  const { yleTunnusInstance } = useTunnusContext();
  const language = useUILanguage();
  const playerScriptUrl = usePlayerScriptUrl();
  const [isPlayerScriptLoaded, setIsPlayerScriptLoaded] = useState(false);
  const carouselRef = useRef<MediaCarouselHandle>(null);
  const visibleItemsCount = useVisibleItemsCount();
  const { ref: wrapperRef, isNear } = useNearViewport();
  const [scrollBounds, setScrollBounds] = useState({
    isAtBeginning: true,
    isAtEnd: true,
  });

  const title = content.title && content.title.trim();
  const { handleItemVisible, handleItemClick } = useVideoFeedTracking(
    contentIndex,
    title
  );

  const scrollLeft = useCallback(() => {
    carouselRef.current?.scrollBy(-visibleItemsCount);
  }, [visibleItemsCount]);

  const scrollRight = useCallback(() => {
    carouselRef.current?.scrollBy(visibleItemsCount);
  }, [visibleItemsCount]);

  return (
    <div className={styles.videoFeedCarousel} data-testid="video-feed-carousel">
      <Script
        src={playerScriptUrl}
        onReady={() => setIsPlayerScriptLoaded(true)}
        onError={(e) => {
          logger.error(e, 'Error while loading player script');
          setIsPlayerScriptLoaded(false);
        }}
      />
      {title && (
        <Container>
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
          </div>
        </Container>
      )}

      <div ref={wrapperRef} className={styles.carouselWrapper}>
        <MediaCarousel
          ref={carouselRef}
          enabled={isNear}
          apiBaseUrl={apiGatewayApiProperties.urlBase}
          appId={apiGatewayApiProperties.appId}
          appKey={apiGatewayApiProperties.appKey}
          variant="areena"
          tunnus={yleTunnusInstance ?? null}
          language={language}
          insetHorizontal={'var(--carousel-inset-horizontal)'}
          cardWidth={'var(--carousel-card-width)'}
          gap={'var(--carousel-gap)'}
          getItemHref={(articleId) =>
            `https://yle.fi/v/video/${articleId}?source=areena`
          }
          onItemVisible={handleItemVisible}
          onItemClick={handleItemClick}
          onScrollBoundsChange={setScrollBounds}
          {...(isPlayerScriptLoaded
            ? { ylePlayer: window.ylePlayer as PlayerSdk }
            : {})}
        />
        <div className={styles.scrollButtonsWrapper}>
          <span
            className={classNames(styles.scrollButton, styles.scrollButtonLeft)}
          >
            <ScrollButton
              direction="left"
              hidden={scrollBounds.isAtBeginning}
              onClick={scrollLeft}
            />
          </span>

          <span
            className={classNames(
              styles.scrollButton,
              styles.scrollButtonRight
            )}
          >
            <ScrollButton
              direction="right"
              hidden={scrollBounds.isAtEnd}
              onClick={scrollRight}
            />
          </span>
        </div>
      </div>
    </div>
  );
};
